import axios from 'axios';
import { Helpers } from '../helpers/Helpers';
import { MesaiSiparisi } from './MesaiKaydiService';
import { MesaiNoktasiPoSatiri } from './MesaiNoktasiService';

/**
 * Ek Mesai Talepleri (Kargo Modülü > Nakliye, QR Mesai Faz 2, 18.10.2026; web ve mobil ortak uçlar).
 * Planlamacı gün + depo + Müşteri · Bölüm · PO · PO No + saat aralığı + kişi sayısı girer; Lojistik QR mesaisi
 * talebin aralığıyla kesişirse yerleşir, ilk kişiyle talebin siparişi açılır.
 * Yetki: menü "Ek Mesai Talepleri" ya da görevde Mesai Planlama.
 */

export const TALEP_DURUMLARI = [
    { value: 'PLAN', label: 'Planlanan' },
    { value: 'ONAY_BEKLIYOR', label: 'Onay Bekliyor' },
    { value: 'REVIZE', label: 'Revize' },
    { value: 'BEKLIYOR', label: 'Mesai Bekliyor' },
    { value: 'TAMAMLANAN', label: 'Tamamlanan' },
];

export type TalepDurumu = 'PLAN' | 'ONAY_BEKLIYOR' | 'REVIZE' | 'BEKLIYOR' | 'TAMAMLANAN';

export interface MesaiTalebiOnayKaydi {
    islem: number
    islemAdi: string
    aciklama?: string | null
    kullanici?: string | null
    tarih: string
}

export interface MesaiTalebiSecenekleri {
    /** Yalnız Depo türündeki QR noktaları. */
    depolar: { mesaiNoktasiId: string, ad: string, musteriler?: { musteriId: string, bolumIdleri: string[] }[] }[]
    /** Kullanıcı tek depoda yetkiliyse o depo (#507). */
    varsayilanDepoId?: string | null
    /** Kullanıcının depo yetkisi var mı (varsa depolar yalnız yetkili olduğu depolar). */
    depoYetkisiVar?: boolean
    poSatirlari: MesaiNoktasiPoSatiri[]
    /** Çalışma Saatleri'ndeki talep aralıkları: "08:00-18:00". */
    araliklar: string[]
    /** Depo yetkili görevlerdeki personel. */
    personeller: { personnelId: string, ad: string }[]
}

export interface MesaiTalebiAyrilan {
    personnelId: string
    ad: string
    /** Kişi talebe yerleşti mi. */
    geldi: boolean
    /** Ek mesai daveti (#514): 1 Bekliyor, 2 Kalıyor, 3 Kalamıyor; boş ise davetsiz eski satır. */
    davetDurumu?: number | null
    /** "dd.MM.yyyy HH:mm" */
    davetZamani?: string | null
    yanitZamani?: string | null
    /** Davetten yanıta geçen süre: "12 dk", "1 sa 5 dk". */
    yanitSuresi?: string | null
    redNotu?: string | null
    /** Kişinin bildirim alabilecek telefon kaydı var mı. */
    telefonVar?: boolean
}

export interface MesaiTalebiYerlesen {
    mesaiKaydiId: string
    personnelId: string
    ad: string
    /** Faturalanan aralık, ör. "18:00–20:15". */
    faturaAraligi: string
    faturaDk: number
}

export interface MesaiTalebiItem {
    mesaiTalebiId: string
    /** "yyyy-MM-dd" */
    tarih: string
    /** "dd.MM.yyyy" */
    tarihStr: string
    mesaiNoktasiId: string
    depoAdi: string
    companyPoId: string
    poEtiketi: string
    customerName: string | null
    divisionName: string | null
    poName: string | null
    poNo: string | null
    kisiSayisi: number
    /** "HH:mm" */
    baslangic: string
    bitis: string
    /** "18:00–22:00" */
    aralik: string
    ayrilanlar: MesaiTalebiAyrilan[]
    /** Daveti reddedenler: yer tutmaz, yeniden seçilemez. */
    reddedenler?: MesaiTalebiAyrilan[]
    /** Talepte davet gitmiş kişi var. */
    davetModu?: boolean
    /** Sunucu kuralı: onay akışı, günü gelmedi / günü geçti, yerleşen eksik / günü geçti, doldu ya da faturalandı. */
    durum: TalepDurumu
    /** Son revize açıklaması (talep revizedeyken). */
    revizeNotu?: string | null
    onayGecmisi?: MesaiTalebiOnayKaydi[]
    /** Talep bittikten sonra davet gönderilmiş: düzenlerken personel zorunlu. */
    gecmisDavet?: boolean
    yerlesenler: MesaiTalebiYerlesen[]
    yerlesenSayisi: number
    eksik: number
    siparis: MesaiSiparisi | null
    /** Siparişteki beklenen gelir (KDV hariç). */
    tutar: number | null
    faturali: boolean
    /** Tarih bugünden önce. */
    tamamlandi: boolean
    giren: string | null
    createdDate: string | null
    /** Sözleşmede yok; sunucu gönderirse güncelleme penceresine dolar. */
    aciklama?: string | null
    /** Liste ekranında satır seçimi (yalnız ekranda). */
    isSelected?: boolean
}

export interface MesaiTalebiListeIstegi {
    /** "yyyy-MM-dd" */
    baslangic?: string | null
    bitis?: string | null
    durum?: TalepDurumu | null
}

export interface MesaiTalebiKaydetIstegi {
    mesaiTalebiId?: string | null
    /** "yyyy-MM-dd" */
    tarih: string
    mesaiNoktasiId: string
    companyPoId: string
    kisiSayisi: number
    /** "HH:mm" */
    baslangic: string
    bitis: string
    ayrilanPersonelIdleri: string[]
    aciklama?: string | null
}

interface Yanit<T> { isSuccess: boolean, message?: string, data: T }

const cagir = async <T>(uc: string, govde: any = {}): Promise<Yanit<T>> => {
    try {
        console.log('SERVICE CALL - MesaiTalebi/' + uc, JSON.stringify(govde));
        const resp = await axios.post(Helpers.getEndpoint() + '/MesaiTalebi/' + uc, govde);
        return Helpers.dataParser(resp);
    } catch (error) {
        throw Helpers.parseErrorMessage(error);
    }
}

/** Talep formunda seçilen personelin o günkü vardiyası, izni ve çalışma süresi uyarıları (İK Faz 3). */
export interface TalepPersonelDurumu {
    personnelId: string
    personelAdi: string | null
    vardiyaKodu: string | null
    vardiyaSaati: string | null
    tatil: boolean
    izinKodu: string | null
    izinAdi: string | null
    onerilenBaslangic: string | null
    haftalikDk: number
    yillikEkDk: number
    uyarilar: string[]
}

export class MesaiTalebiService {
    static secenekler = () => cagir<MesaiTalebiSecenekleri>('Secenekler');
    static liste = (istek: MesaiTalebiListeIstegi) => cagir<MesaiTalebiItem[]>('Liste', istek);
    static kaydet = (istek: MesaiTalebiKaydetIstegi) => cagir<MesaiTalebiItem>('Kaydet', istek);
    static kisiDegistir = (mesaiTalebiId: string, kisiSayisi: number) => cagir<MesaiTalebiItem>('KisiDegistir', { mesaiTalebiId, kisiSayisi });
    static sil = (ids: string[]) => cagir<boolean>('Sil', { ids });
    static personelDurumlari = (tarih: string, baslangic: string | null, bitis: string | null, personelIdleri: string[]) => cagir<TalepPersonelDurumu[]>('PersonelDurumlari', { tarih, baslangic, bitis, personelIdleri });
}
